import React from 'react';
import { makeStyles } from '@mui/styles';
import MailIcon from '@mui/icons-material/Mail';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { SUPPORT_MAIL } from '../utils/constants';
import MultiOrg from '../components/MultiOrg';

const useStyles = makeStyles(theme => ({
  content: {
    flexGrow: 80,
    padding: theme.spacing(3),
    width: '100%',
    boxSizing: "border-box",
    overflow: 'hidden',
    '@media (max-width: 600px)': {
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      maxWidth: '100vw',
      overflowX: 'hidden',
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    '@media (max-width: 600px)': {
      minHeight: '120px',
    },
  },

  // Desktop Layout - Simple listing style
  desktopContainer: {
    display: "block",
    '@media (max-width: 600px)': {
      display: "none",
    },
  },
  listingCard: {
    background: "#fff",
    borderRadius: "8px",
    padding: "20px 24px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    border: "1px solid #f0f0f0",
    marginBottom: "16px",
  },
  listingRow: {
    display: "flex",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #f5f5f5",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  listingIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(111, 41, 129, 0.1)",
    color: "#6f2981",
    marginRight: "16px",
  },
  listingLabel: {
    fontSize: "14px",
    color: "#666",
    minWidth: "150px",
  },
  listingValue: {
    fontSize: "14px",
    color: "#333",
    fontWeight: 500,
    flex: 1,
  },
  emailLink: {
    color: "#6f2981",
    fontWeight: 600,
    textDecoration: "none",
    wordBreak: "break-all",
    "&:hover": {
      textDecoration: "underline",
    },
  },

  // Tablet Layout - Card style (now hidden, using desktop for tablet)
  tabletContainer: {
    display: "none",
  },
  tabletCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
    border: "1px solid #f0f0f0",
    marginBottom: "16px",
  },
  tabletHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #f0f0f0",
  },
  tabletIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(111, 41, 129, 0.1)",
    color: "#6f2981",
    marginRight: "12px",
  },
  tabletTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#333",
    margin: 0,
  },
  tabletRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  tabletLabel: {
    fontSize: "13px",
    color: "#888",
    minWidth: "100px",
  },
  tabletValue: {
    fontSize: "14px",
    color: "#333",
    fontWeight: 500,
  },

  // Mobile Layout - Compact cards
  mobileContainer: {
    display: "none",
    '@media (max-width: 600px)': {
      display: "block",
      width: "100%",
      overflowX: "hidden",
    },
  },
  mobileCard: {
    background: "#fff",
    borderRadius: "10px",
    padding: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    border: "1px solid #f0f0f0",
    marginBottom: "10px",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  mobileHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
  },
  mobileIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(111, 41, 129, 0.1)",
    color: "#6f2981",
    marginRight: "10px",
  },
  mobileTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#333",
    margin: 0,
  },
  mobileRow: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "8px",
    fontSize: "13px",
    width: "100%",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  mobileLabel: {
    color: "#888",
    minWidth: "60px",
    flexShrink: 0,
  },
  mobileValue: {
    color: "#333",
    fontWeight: 500,
    wordBreak: "break-all",
    overflow: "hidden",
    flex: 1,
  },

  helpText: {
    color: "#555",
    fontSize: "14px",
    lineHeight: 1.6,
    margin: 0,
    '@media (max-width: 600px)': {
      fontSize: "13px",
    },
  },
}));

const Support = (props) => {
  const classes = useStyles();
  const { typeButton, handleHome } = props;

  return (
    <main className={classes.content}>
      <div className={classes.toolbar} />
      <div>
        <MultiOrg handleHome={handleHome} typeButton={typeButton} />
      </div>

      {typeButton === 'Support' ? (
        <>
          {/* Desktop Layout */}
          <div className={classes.desktopContainer}>
            <div className={classes.listingCard}>
              <div className={classes.listingRow}>
                <div className={classes.listingIcon}>
                  <MailIcon />
                </div>
                <span className={classes.listingLabel}>Contact Email</span>
                <span className={classes.listingValue}>
                  <a href={`mailto:${SUPPORT_MAIL}`} className={classes.emailLink}>
                    {SUPPORT_MAIL}
                  </a>
                </span>
              </div>
              <div className={classes.listingRow}>
                <div className={classes.listingIcon}>
                  <AccessTimeIcon />
                </div>
                <span className={classes.listingLabel}>Availability</span>
                <span className={classes.listingValue}>24 hours x 7 days</span>
              </div>
              <div className={classes.listingRow}>
                <div className={classes.listingIcon}>
                  <HelpOutlineIcon />
                </div>
                <span className={classes.listingLabel}>How to Get Help</span>
                <span className={classes.listingValue}>
                  Send an email with your login credentials and screenshot of the error
                </span>
              </div>
            </div>
          </div>

          {/* Tablet Layout */}
          <div className={classes.tabletContainer}>
            <div className={classes.tabletCard}>
              <div className={classes.tabletHeader}>
                <div className={classes.tabletIcon}>
                  <MailIcon />
                </div>
                <h3 className={classes.tabletTitle}>Email Support</h3>
              </div>
              <div className={classes.tabletRow}>
                <span className={classes.tabletLabel}>Email:</span>
                <a href={`mailto:${SUPPORT_MAIL}`} className={classes.emailLink}>
                  {SUPPORT_MAIL}
                </a>
              </div>
              <div className={classes.tabletRow}>
                <span className={classes.tabletLabel}>Availability:</span>
                <span className={classes.tabletValue}>24 hours x 7 days</span>
              </div>
            </div>
            <div className={classes.tabletCard}>
              <div className={classes.tabletHeader}>
                <div className={classes.tabletIcon}>
                  <HelpOutlineIcon />
                </div>
                <h3 className={classes.tabletTitle}>How to Get Help</h3>
              </div>
              <p className={classes.helpText}>
                Send an email with your login credentials and screenshot of the error to {SUPPORT_MAIL}
              </p>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className={classes.mobileContainer}>
            <div className={classes.mobileCard}>
              <div className={classes.mobileHeader}>
                <div className={classes.mobileIcon}>
                  <MailIcon style={{ fontSize: 20 }} />
                </div>
                <h3 className={classes.mobileTitle}>Email Support</h3>
              </div>
              <div className={classes.mobileRow}>
                <span className={classes.mobileLabel}>Email:</span>
                <a href={`mailto:${SUPPORT_MAIL}`} className={`${classes.mobileValue} ${classes.emailLink}`}>
                  {SUPPORT_MAIL}
                </a>
              </div>
              <div className={classes.mobileRow}>
                <span className={classes.mobileLabel}>Hours:</span>
                <span className={classes.mobileValue}>24 x 7</span>
              </div>
            </div>
            <div className={classes.mobileCard}>
              <div className={classes.mobileHeader}>
                <div className={classes.mobileIcon}>
                  <HelpOutlineIcon style={{ fontSize: 20 }} />
                </div>
                <h3 className={classes.mobileTitle}>How to Get Help</h3>
              </div>
              <p className={classes.helpText}>
                Email your login credentials and error screenshot to {SUPPORT_MAIL}
              </p>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* User Help - Desktop */}
          <div className={classes.desktopContainer}>
            <div className={classes.listingCard}>
              <div className={classes.listingRow}>
                <div className={classes.listingIcon}>
                  <HelpOutlineIcon />
                </div>
                <span className={classes.listingLabel}>User Help</span>
                <span className={classes.listingValue}>
                  Please check the new tab for detailed help documentation and guides
                </span>
              </div>
            </div>
          </div>

          {/* User Help - Tablet */}
          <div className={classes.tabletContainer}>
            <div className={classes.tabletCard}>
              <div className={classes.tabletHeader}>
                <div className={classes.tabletIcon}>
                  <HelpOutlineIcon />
                </div>
                <h3 className={classes.tabletTitle}>User Help</h3>
              </div>
              <p className={classes.helpText}>
                Please check the new tab for detailed help documentation and guides on how to use the platform.
              </p>
            </div>
          </div>

          {/* User Help - Mobile */}
          <div className={classes.mobileContainer}>
            <div className={classes.mobileCard}>
              <div className={classes.mobileHeader}>
                <div className={classes.mobileIcon}>
                  <HelpOutlineIcon style={{ fontSize: 20 }} />
                </div>
                <h3 className={classes.mobileTitle}>User Help</h3>
              </div>
              <p className={classes.helpText}>
                Check the new tab for help documentation.
              </p>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default Support;
